import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { RateTableViewer, matchTier } from "@/components/RateTableViewer";
import {
  HandWaving,
  Plus,
  Truck,
  Package,
  ArrowRight,
  X,
  CheckCircle,
  Clock,
} from "@phosphor-icons/react";

const TIME_WINDOWS = [
  { v: "morning", label: "Morning (8am – 12pm)" },
  { v: "afternoon", label: "Afternoon (12pm – 5pm)" },
  { v: "anytime", label: "Anytime (8am – 5pm)" },
];

// 5pm Jamaica time cutoff. After 5pm same-day pickups are scheduled next day 8am–5pm.
const KG_TO_LB = 2.20462;
const kgToLb = (kg) => {
  const v = parseFloat(kg);
  if (!v || isNaN(v)) return "";
  return (v * KG_TO_LB).toFixed(2);
};
const fmtKgLb = (kg) => {
  const v = parseFloat(kg);
  if (!v || isNaN(v)) return null;
  return `${v.toFixed(2)} kg / ${(v * KG_TO_LB).toFixed(2)} lb`;
};

const STATUS_BADGE = {
  requested: { bg: "bg-amber-50 dark:bg-amber-500/10", fg: "text-amber-700 dark:text-amber-300", bdr: "border-amber-200 dark:border-amber-500/30", label: "Requested" },
  scheduled: { bg: "bg-blue-50 dark:bg-blue-500/10", fg: "text-blue-700 dark:text-blue-300", bdr: "border-blue-200 dark:border-blue-500/30", label: "Scheduled" },
  picked_up: { bg: "bg-emerald-50 dark:bg-emerald-500/10", fg: "text-emerald-700 dark:text-emerald-300", bdr: "border-emerald-200 dark:border-emerald-500/30", label: "Picked up" },
  converted: { bg: "bg-zinc-100 dark:bg-zinc-800", fg: "text-zinc-700 dark:text-zinc-300", bdr: "border-zinc-200 dark:border-zinc-700", label: "Converted" },
  cancelled: { bg: "bg-red-50 dark:bg-red-500/10", fg: "text-red-700 dark:text-red-300", bdr: "border-red-200 dark:border-red-500/30", label: "Cancelled" },
};

const EMPTY_FORM = {
  pickup_address: "",
  pickup_lat: null,
  pickup_lng: null,
  pickup_contact_name: "",
  pickup_contact_phone: "",
  pickup_date: "",
  pickup_time_window: "anytime",
  package_description: "",
  weight_kg: "",
  dimensions: "",
  declared_value: "",
  destination_name: "",
  destination_address: "",
  destination_lat: null,
  destination_lng: null,
  destination_phone: "",
  destination_email: "",
  notes: "",
  signature_required: false,
  payment_method: "cod",
};

export default function Pickups() {
  const { user } = useAuth();
  const isCustomer = user.role === "customer";
  const canManage = ["company_admin", "dispatcher", "super_admin"].includes(user.role);

  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  // Courier's per-lb rate — lets customers estimate shipping cost from weight
  const [rate, setRate] = useState(null);
  const [rateViewerOpen, setRateViewerOpen] = useState(false);

  useEffect(() => {
    if (!isCustomer) return;
    api.get("/customer/courier-rate").then(({ data }) => setRate(data)).catch(() => {});
    // eslint-disable-next-line
  }, [isCustomer]);

  const load = async () => {
    try {
      setLoading(true);
      const [p, d] = await Promise.all([
        api.get("/pickups"),
        canManage ? api.get("/drivers", { params: { pickers_only: true } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setItems(p.data.items || []);
      setDrivers(Array.isArray(d.data) ? d.data : (d.data.items || []));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.pickup_address || !form.package_description || !form.destination_name || !form.destination_address) {
      toast.error("Pickup address, package description, destination name and address are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
        declared_value: form.declared_value ? parseFloat(form.declared_value) : null,
        pickup_date: form.pickup_date || null,
        // Coerce empty-string optional fields to null so FastAPI's EmailStr / number
        // validators don't 422 on a stray "" submitted from an unfilled input.
        destination_email: (form.destination_email || "").trim() || null,
        destination_phone: (form.destination_phone || "").trim() || null,
        pickup_contact_phone: (form.pickup_contact_phone || "").trim() || null,
        pickup_contact_name: (form.pickup_contact_name || "").trim() || null,
        dimensions: (form.dimensions || "").trim() || null,
        notes: (form.notes || "").trim() || null,
      };
      await api.post("/pickups", payload);
      toast.success("Pickup request submitted! Your courier has been notified.");
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPickup = async (p) => {
    if (!confirm("Cancel this pickup request?")) return;
    try {
      await api.patch(`/pickups/${p.id}`, { status: "cancelled" });
      toast.success("Pickup cancelled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const assignDriver = async (p, driverId) => {
    try {
      const wasReassign = !!p.driver_id && p.driver_id !== driverId;
      await api.patch(`/pickups/${p.id}`, { driver_id: driverId, status: "scheduled" });
      toast.success(wasReassign ? "Driver reassigned" : "Driver assigned");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markPickedUp = async (p) => {
    try {
      await api.patch(`/pickups/${p.id}`, { status: "picked_up" });
      toast.success("Marked as picked up");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const convertToPackage = async (p) => {
    if (!confirm(`Convert pickup to a package? A new tracking number will be generated.`)) return;
    try {
      const { data } = await api.post(`/pickups/${p.id}/convert-to-package`);
      toast.success(`Package created: ${data.tracking_number}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Super-admin overrides
  const toggleHidden = async (p) => {
    try {
      await api.patch(`/pickups/${p.id}`, { hidden_from_drivers: !p.hidden_from_drivers });
      toast.success(p.hidden_from_drivers ? "Pickup now visible to drivers" : "Pickup hidden from drivers");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const overrideSignatureFee = async (p) => {
    const cur = p.signature_surcharge_override ?? 150;
    const raw = prompt(`Signature surcharge for this pickup (JMD). Default is $150.\nEnter a new amount:`, String(cur));
    if (raw === null) return;
    const v = parseFloat(raw);
    if (isNaN(v) || v < 0) { toast.error("Enter a valid amount (0 or greater)"); return; }
    try {
      await api.patch(`/pickups/${p.id}`, { signature_surcharge_override: v });
      toast.success(`Signature surcharge set to $${v.toFixed(2)} JMD`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="pickups-page">
      <Toaster position="top-right" />
      {isCustomer && (
        <RateTableViewer
          open={rateViewerOpen}
          onOpenChange={setRateViewerOpen}
          rates={rate?.rate_table || []}
          currency={rate?.per_lb_rate_currency || "JMD"}
          companyName={rate?.company_name}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
            <Truck size={12} weight="bold" /> Powered by Kanal . ZipExpress
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1 text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>
            Pickup Requests
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            {isCustomer
              ? "Request a pickup — our last-mile partner Kanal ZipExpress collects from you."
              : "Customer pickup requests fulfilled by Kanal ZipExpress. Assign drivers or convert to packages."}
          </p>
        </div>
        {isCustomer && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="new-pickup-btn">
                <Plus size={14} className="mr-1.5" weight="bold" /> Request a pickup
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pickup-form-dialog">
              <DialogHeader>
                <DialogTitle>Request a Pickup</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSubmit} className="space-y-5 mt-2" data-testid="pickup-form">
                {/* Cutoff notice */}
                <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md px-4 py-3 flex gap-2.5 text-xs text-amber-900 dark:text-amber-200" data-testid="pickup-cutoff-notice">
                  <Clock size={14} weight="bold" className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300" />
                  <div>
                    <b>Pickup hours: 8am – 5pm (Jamaica time).</b> Requests submitted after 5pm are automatically scheduled for the next day. Weight is captured in kg with an automatic conversion to lbs.
                  </div>
                </div>

                {/* Pickup */}
                <Section title="Pickup from">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Pickup address *</Label>
                  <div className="mt-1.5">
                    <AddressAutocomplete
                      value={form.pickup_address}
                      onChange={(v) => setForm({ ...form, pickup_address: v })}
                      onLocationChange={(lat, lng) => setForm((f) => ({ ...f, pickup_lat: lat, pickup_lng: lng }))}
                      placeholder="Start typing your address…"
                      rows={2}
                      dataTestId="pickup-address"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <Field label="Contact name (optional)" testid="pickup-contact-name" value={form.pickup_contact_name} onChange={(v) => setForm({ ...form, pickup_contact_name: v })} />
                    <Field label="Contact phone (optional)" testid="pickup-contact-phone" value={form.pickup_contact_phone} onChange={(v) => setForm({ ...form, pickup_contact_phone: v })} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <Field label="Pickup date" type="date" testid="pickup-date" value={form.pickup_date} onChange={(v) => setForm({ ...form, pickup_date: v })} />
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Time window</Label>
                      <Select value={form.pickup_time_window} onValueChange={(v) => setForm({ ...form, pickup_time_window: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="pickup-time-window"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIME_WINDOWS.map((w) => <SelectItem key={w.v} value={w.v}>{w.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </Section>

                {/* Package */}
                <Section title="Package details">
                  <Field label="Package description *" testid="pickup-description" value={form.package_description} onChange={(v) => setForm({ ...form, package_description: v })} required />
                  <div className="grid sm:grid-cols-3 gap-3 mt-3">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Weight</Label>
                      <div className="mt-1.5 relative">
                        <Input
                          type="number"
                          step="0.1"
                          value={form.weight_kg}
                          onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                          data-testid="pickup-weight"
                          placeholder="0.0"
                          className="pr-12"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 font-medium pointer-events-none">kg</span>
                      </div>
                      {form.weight_kg && (
                        <div className="text-[11px] text-zinc-500 mt-1" data-testid="pickup-weight-lbs">
                          ≈ <b>{kgToLb(form.weight_kg)} lb</b>
                          {(() => {
                            const lbs = parseFloat(kgToLb(form.weight_kg));
                            if (!lbs) return null;
                            const sym = (c) => (c === "USD" ? "US$" : "J$");
                            // Prefer the weight-tier rate table if available
                            if (rate?.rate_table?.length > 0) {
                              const tier = matchTier(rate.rate_table, lbs);
                              if (!tier) return null;
                              const cur = rate.per_lb_rate_currency || "JMD";
                              return (
                                <>
                                  <span data-testid="pickup-est-cost">
                                    {" "}· est. shipping{" "}
                                    <b>
                                      {sym(cur)}{Number(tier.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </b>{" "}
                                    ({tier.lbs} lb tier)
                                  </span>
                                  {" "}·{" "}
                                  <button
                                    type="button"
                                    onClick={() => setRateViewerOpen(true)}
                                    className="text-cyan-700 hover:underline font-semibold"
                                    data-testid="pickup-view-rates-btn"
                                  >
                                    View full table
                                  </button>
                                </>
                              );
                            }
                            if (rate?.per_lb_rate != null) {
                              return (
                                <span data-testid="pickup-est-cost">
                                  {" "}· est. shipping{" "}
                                  <b>
                                    {sym(rate.per_lb_rate_currency)}{(lbs * rate.per_lb_rate).toFixed(2)}
                                  </b>{" "}
                                  @ {sym(rate.per_lb_rate_currency)}{rate.per_lb_rate}/lb
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                    <Field label="Dimensions (LxWxH cm)" testid="pickup-dimensions" value={form.dimensions} onChange={(v) => setForm({ ...form, dimensions: v })} />
                    <Field label="Declared value ($)" type="number" testid="pickup-value" value={form.declared_value} onChange={(v) => setForm({ ...form, declared_value: v })} />
                  </div>
                </Section>

                {/* Destination */}
                <Section title="Deliver to">
                  <Field label="Recipient name *" testid="pickup-dest-name" value={form.destination_name} onChange={(v) => setForm({ ...form, destination_name: v })} required />
                  <div className="mt-3">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Destination address *</Label>
                    <div className="mt-1.5">
                      <AddressAutocomplete
                        value={form.destination_address}
                        onChange={(v) => setForm({ ...form, destination_address: v })}
                        onLocationChange={(lat, lng) => setForm((f) => ({ ...f, destination_lat: lat, destination_lng: lng }))}
                        placeholder="Start typing destination address…"
                        rows={2}
                        dataTestId="pickup-dest-address"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <Field label="Recipient phone" testid="pickup-dest-phone" value={form.destination_phone} onChange={(v) => setForm({ ...form, destination_phone: v })} />
                    <Field label="Recipient email" type="email" testid="pickup-dest-email" value={form.destination_email} onChange={(v) => setForm({ ...form, destination_email: v })} />
                  </div>
                </Section>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Notes (optional)</Label>
                  <Textarea className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Special instructions, gate code, etc." rows={2} data-testid="pickup-notes" />
                </div>

                {/* Module 2 — signature required + JMD 150 surcharge consent */}
                <label className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 px-3.5 py-3 cursor-pointer hover:bg-amber-100/60 dark:hover:bg-amber-500/15 transition-colors" data-testid="pickup-signature-required-label">
                  <input
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 rounded text-amber-600 focus:ring-amber-500"
                    checked={!!form.signature_required}
                    onChange={(e) => setForm({ ...form, signature_required: e.target.checked })}
                    data-testid="pickup-signature-required"
                  />
                  <div className="text-sm">
                    <div className="font-semibold text-zinc-900 dark:text-zinc-100">Require a signature on delivery?</div>
                    <div className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                      The driver must capture the recipient's signature at the door before marking the package delivered.
                      An extra <b>$150 JMD</b> handling fee will be added to your receipt for this service.
                    </div>
                  </div>
                </label>

                {/* Payment method — required choice (PRD: COD vs Pay Now via WiPay) */}
                <div data-testid="pickup-payment-method-section">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">How would you like to pay for this pickup? *</Label>
                  <p className="text-xs text-zinc-500 mt-0.5 mb-2">Your courier will invoice you based on the package. Pick how you want to settle that invoice.</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <label className={`cursor-pointer rounded-md border px-3.5 py-3 transition-colors ${form.payment_method === "cod" ? "border-[#002BF6] bg-[#002BF6]/5" : "border-zinc-200 hover:border-zinc-300"}`} data-testid="pickup-pay-cod-label">
                      <input
                        type="radio"
                        name="pickup-payment-method"
                        value="cod"
                        checked={form.payment_method === "cod"}
                        onChange={() => setForm({ ...form, payment_method: "cod" })}
                        className="mr-2 align-middle"
                        data-testid="pickup-pay-cod"
                      />
                      <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Cash on Delivery (COD)</span>
                      <div className="text-xs text-zinc-600 dark:text-zinc-300 mt-1">Pay the driver in cash when your package arrives.</div>
                    </label>
                    <label className={`cursor-pointer rounded-md border px-3.5 py-3 transition-colors ${form.payment_method === "wipay" ? "border-[#002BF6] bg-[#002BF6]/5" : "border-zinc-200 hover:border-zinc-300"}`} data-testid="pickup-pay-wipay-label">
                      <input
                        type="radio"
                        name="pickup-payment-method"
                        value="wipay"
                        checked={form.payment_method === "wipay"}
                        onChange={() => setForm({ ...form, payment_method: "wipay" })}
                        className="mr-2 align-middle"
                        data-testid="pickup-pay-wipay"
                      />
                      <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Pay Now (WiPay)</span>
                      <div className="text-xs text-zinc-600 dark:text-zinc-300 mt-1">Secure card payment via WiPay once your invoice is issued.</div>
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={submitting} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="pickup-submit-btn">
                    {submitting ? "Submitting…" : "Submit pickup request"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setForm(EMPTY_FORM); }}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-12 text-center" data-testid="pickups-empty">
          <HandWaving size={28} weight="bold" className="mx-auto text-zinc-400 mb-3" />
          <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">No pickup requests yet</h3>
          <p className="text-sm text-zinc-500">{isCustomer ? "Click 'Request a pickup' to schedule one." : "Customer pickup requests will appear here."}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pickups-list">
          {items.map((p) => <PickupCard
            key={p.id} p={p}
            isCustomer={isCustomer} canManage={canManage}
            isSuperAdmin={user.role === "super_admin"}
            drivers={drivers}
            onCancel={cancelPickup} onAssign={assignDriver}
            onPickedUp={markPickedUp} onConvert={convertToPackage}
            onToggleHidden={toggleHidden} onOverrideSignature={overrideSignatureFee}
          />)}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800 rounded-md p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required = false, step }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      <Input type={type} step={step} className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}

function PickupCard({ p, isCustomer, canManage, isSuperAdmin, drivers, onCancel, onAssign, onPickedUp, onConvert, onToggleHidden, onOverrideSignature }) {
  const status = STATUS_BADGE[p.status] || STATUS_BADGE.requested;
  const sigAmount = p.signature_surcharge_override ?? 150;
  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5" data-testid={`pickup-card-${p.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${status.bg} ${status.fg} ${status.bdr}`} data-testid={`pickup-status-${p.id}`}>
              {p.status === "requested" && <Clock size={9} weight="bold" />}
              {p.status === "picked_up" && <CheckCircle size={9} weight="bold" />}
              {p.status === "converted" && <Package size={9} weight="bold" />}
              {status.label}
            </span>
            <span className="text-xs text-zinc-500">{new Date(p.created_at).toLocaleString()}</span>
            {!isCustomer && p.customer_number && (
              <code className="text-[10px] bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded font-mono text-zinc-700 dark:text-zinc-300">{p.customer_number}</code>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <Pair k="Pickup" v={p.pickup_address} />
            <Pair k="Destination" v={p.destination_address} />
            <Pair k="Package" v={`${p.package_description}${p.weight_kg ? ` · ${fmtKgLb(p.weight_kg)}` : ""}`} />
            <Pair k="When" v={
              <span>
                {p.pickup_date || "ASAP"} · {p.pickup_time_window || "anytime"}
                {p.auto_scheduled_next_day && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 rounded-full px-2 py-0.5" data-testid={`pickup-next-day-${p.id}`}>
                    <Clock size={9} weight="bold" /> Auto-scheduled (after 5pm cutoff)
                  </span>
                )}
              </span>
            } />
            {!isCustomer && <Pair k="Customer" v={`${p.customer_name} (${p.customer_email})`} />}
            <Pair k="To" v={`${p.destination_name}${p.destination_phone ? ` · ${p.destination_phone}` : ""}`} />
            {p.driver_name && <Pair k="Driver" v={p.driver_name} />}
            {p.signature_required && <Pair k="Signature" v={<span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 rounded-full px-2 py-0.5" data-testid={`pickup-signature-badge-${p.id}`}>Required · +${Number(sigAmount).toFixed(2)} JMD{p.signature_surcharge_override != null ? " (override)" : ""}</span>} />}
            {p.payment_method && <Pair k="Payment" v={<span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${p.payment_method === "wipay" ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30" : "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30"}`} data-testid={`pickup-payment-badge-${p.id}`}>{p.payment_method === "wipay" ? "Pay Now · WiPay" : "Cash on Delivery"}</span>} />}
            {p.estimated_total != null && (
              <Pair k="Est. cost" v={
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide text-zinc-800 dark:text-zinc-200" data-testid={`pickup-est-${p.id}`}>
                  ${Number(p.estimated_total).toFixed(2)} JMD
                  <span className="text-[10px] font-normal text-zinc-500">
                    (pickup ${Number(p.estimated_pickup_fee || 0).toFixed(2)}{p.signature_required ? ` + sig ${Number(sigAmount).toFixed(2)}` : ""})
                  </span>
                </span>
              } />
            )}
            {p.hidden_from_drivers && (
              <Pair k="Visibility" v={<span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30 rounded-full px-2 py-0.5" data-testid={`pickup-hidden-${p.id}`}>Hidden from drivers</span>} />
            )}
            {p.converted_tracking_number && <Pair k="Tracking" v={<code className="font-mono text-xs">{p.converted_tracking_number}</code>} />}
          </div>
          {p.notes && <div className="mt-3 text-xs text-zinc-600 dark:text-zinc-400 italic">Notes: {p.notes}</div>}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {isCustomer && (p.status === "requested" || p.status === "scheduled") && (
            <Button size="sm" variant="outline" onClick={() => onCancel(p)} data-testid={`pickup-cancel-${p.id}`}>
              <X size={12} className="mr-1" /> Cancel
            </Button>
          )}
          {canManage && (p.status === "requested" || p.status === "scheduled") && drivers.length > 0 && (
            <Select onValueChange={(v) => onAssign(p, v)}>
              <SelectTrigger className="h-8 w-[180px] text-xs" data-testid={`pickup-assign-${p.id}`}><SelectValue placeholder={p.driver_name ? `Change driver…` : `Assign driver…`} /></SelectTrigger>
              <SelectContent>
                {drivers.filter((d) => d.active !== false).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canManage && p.status === "scheduled" && (
            <Button size="sm" variant="outline" onClick={() => onPickedUp(p)} data-testid={`pickup-mark-${p.id}`}>
              <CheckCircle size={12} className="mr-1" /> Mark picked up
            </Button>
          )}
          {canManage && p.status !== "converted" && p.status !== "cancelled" && (
            <Button size="sm" className="bg-[#002BF6] hover:bg-[#0021C7] text-white" onClick={() => onConvert(p)} data-testid={`pickup-convert-${p.id}`}>
              <ArrowRight size={12} className="mr-1" /> Convert to package
            </Button>
          )}
          {isSuperAdmin && p.status !== "converted" && p.status !== "cancelled" && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onToggleHidden(p)}
                className={p.hidden_from_drivers ? "border-red-300 text-red-700 hover:bg-red-50" : "border-zinc-300"}
                data-testid={`pickup-toggle-hidden-${p.id}`}
                title={p.hidden_from_drivers ? "Make this pickup visible to drivers" : "Hide this pickup from drivers"}
              >
                {p.hidden_from_drivers ? "Show to drivers" : "Hide from drivers"}
              </Button>
              {p.signature_required && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onOverrideSignature(p)}
                  data-testid={`pickup-override-sig-${p.id}`}
                  title="Override the JMD 150 signature surcharge for this pickup"
                >
                  Edit signature fee
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Pair({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{k}</div>
      <div className="text-zinc-800 dark:text-zinc-200 mt-0.5">{v || "—"}</div>
    </div>
  );
}
